import { request, qs } from "./http";
import type {
  ExteriorDoorInitResponse,
  ExteriorDoorLoadResponse,
  ExteriorDoorOrderItem,
  ExteriorDoorOrderResponse,
  ExteriorDoorState,
  ExteriorDoorUpdateResponse,
} from "@/modules/exterior-door/store/types";
import type { UpAndOverDoorState, UpAndOverInitResponse, UpAndOverUpdateResponse, UpAndOverLoadResponse, UpAndOverOrderResponse } from "@/modules/up-and-over/store/upAndOverStore";
import type { GarageInitResponse, GarageLoadResponse, GarageUpdateResponse } from "@/modules/garage/store/types";
import type {
  Analytics,
  DealerName,
  AnalyticsRangeName,
  TenantSummary,
  TenantEditor,
  SaveTenantRequest,
  SaveTenantTrackingRequest,
  SaveBrochureSlotRequest,
  TenantTracking,
  TenantBrochures,
  PdfDocumentKind,
  TenantConfigResponse,
  UserInfo,
  SessionContext,
  ResolveTokenResponse,
  ProjectQueryParams,
  ProjectListResponse,
  ProjectStats,
  ProjectRevision,
  ProjectItemSummary,
  SaveProjectRequest,
  SaveProjectResponse,
  DealerSummaryDto,
  GarageSummaryResponse,
  PriceSummary,
  ProductCost,
  ModelTransformOverride,
  Vec3D,
  PlaceOrderResponse,
  FaqItem,
  ReleaseNotes,
  OrderJobDto,
  OrderJobActionResult,
  AdminUser,
  AppPermissionName,
  EmailPreviewRequest,
  CacheRefreshStatus,
  CacheRefreshStarted,
  PriceListReloaded,
  Notice,
  SaveNoticeRequest,
  Campaign,
  SaveCampaignRequest,
  QuoteRequestView,
  Ga4ValidationReport,
} from "./types";

// ── Deadlines for the calls that are slow by nature (see RequestOptions.timeoutMs) ───────────
// An internal order writes the whole configuration tree to D365; the server gives it five minutes.
const ORDER_TIMEOUT_MS = 330_000;
// Rendering documents and sending a batch of mail.
const DOCUMENT_TIMEOUT_MS = 120_000;
// The privacy passes walk the whole project table.
const ADMIN_RUN_TIMEOUT_MS = 300_000;
// A cost price builds the product's whole D365 tree in the engine and reads a price for every part in it. The
// server gives the engine three minutes (ProductCostService.EngineTimeout); this waits a little longer than that.
const COST_TIMEOUT_MS = 200_000;
// The price-list reload: two minutes for the engine to read the workbook (EngineAdminClient.PriceListTimeout),
// then the catalog fetched from it.
const PRICE_LIST_TIMEOUT_MS = 150_000;

// ── Typed API methods ────────────────────────────────────────────────────────

export const api = {
  tenant: {
    config: () => request<TenantConfigResponse>("/tenant/config"),
  },

  user: {
    me: () => request<UserInfo>("/user/me"),
  },

  session: {
    context: () => request<SessionContext>("/session/context"),
    resolveToken: (token: string) =>
      request<ResolveTokenResponse>("/session/resolve-token", { method: "POST", body: { token } }),
  },

  dashboard: {
    getProjects: (params: ProjectQueryParams = {}) =>
      request<ProjectListResponse>(`/dashboard/projects${qs({
        extension: params.extension,
        includeArchived: params.includeArchived,
        search: params.search,
        sortBy: params.sortBy,
        sortDesc: params.sortDesc,
        page: params.page,
        pageSize: params.pageSize,
        status: params.status,
        favoritesOnly: params.favoritesOnly,
        tenant: params.tenant,
      })}`),

    getStats: (extension?: string, tenant?: string) =>
      request<ProjectStats>(`/dashboard/projects/stats${qs({ extension, tenant })}`),

    // Earlier versions of a project's job — the dashboard lists only the newest of each.
    projectRevisions: (id: number) =>
      request<ProjectRevision[]>(`/dashboard/projects/${id}/revisions`),

    // The products a row holds, for the bundle popover — read on demand, so the list stays cheap.
    getProjectItems: (id: number) =>
      request<ProjectItemSummary[]>(`/dashboard/projects/${id}/items`),

    saveProject: (data: SaveProjectRequest) =>
      request<SaveProjectResponse>("/dashboard/projects", { method: "POST", body: data }),

    deleteProject: (id: number) =>
      request<void>(`/dashboard/projects/${id}`, { method: "DELETE" }),

    toggleFavorite: (id: number) =>
      request<{ isFavorite: boolean }>(`/dashboard/projects/${id}/favorite`, { method: "PUT" }),

    toggleArchive: (id: number) =>
      request<{ archived: boolean }>(`/dashboard/projects/${id}/archive`, { method: "PUT" }),

    duplicateProject: (id: number) =>
      request<SaveProjectResponse>(`/dashboard/projects/${id}/duplicate`, { method: "POST" }),

    renameProject: (id: number, name: string) =>
      request<{ name: string }>(`/dashboard/projects/${id}/rename`, { method: "PUT", body: { name } }),
  },

  customers: {
    // LOBAS dealers nearest the customer's postal code — the basic "Request quotation" modal lists
    // these. The server ranks them and returns only what the picker renders (no contact details).
    // `search` looks through the WHOLE directory (name, address, city, postal code); `take` caps the
    // result. The picker shows a short list and searches the full one, so the two are independent.
    // The company-chain filter is applied server-side from the request host — not passed from here.
    // `accountNumber` pins the answer to one dealer (the ?dealer= link, see lockedDealer) and overrides
    // proximity and search, which mean nothing against a list of one.
    nearestDealers: (
      postalCode?: string | null, take?: number, search?: string | null, accountNumber?: string | null,
    ) =>
      request<DealerSummaryDto[]>(`/dealers/nearest${qs({
        postalCode, take, search: search || undefined, accountNumber: accountNumber || undefined,
      })}`),

    // Postal place for a Norwegian postal code (Bring, proxied server-side). `city` is null for a code
    // that does not exist — an unknown code is a normal thing to type, not a failure.
    postalPlace: (code: string) =>
      request<{ postalCode: string; city: string | null }>(`/postal-codes/${encodeURIComponent(code)}`),
  },

  // The door's own calls. Configuring and pricing serve both entries — a door beside a garage door,
  // which the `garage` order places, and doors ordered on their own, which order/load handle (internal).
  exteriorDoor: {
    init: () => request<ExteriorDoorInitResponse>("/exterior-door/init"),
    update: (state: ExteriorDoorState) =>
      request<ExteriorDoorUpdateResponse>("/exterior-door/update", { method: "POST", body: state }),
    cost: (state: ExteriorDoorState) =>
      request<ProductCost>("/exterior-door/cost", { method: "POST", body: state, timeoutMs: COST_TIMEOUT_MS }),
    load: (projectId: number) => request<ExteriorDoorLoadResponse>(`/exterior-door/load/${projectId}`),
    order: (body: { items: ExteriorDoorOrderItem[]; projectName: string; projectId: number | null; salesId: string | null }) =>
      request<ExteriorDoorOrderResponse>("/exterior-door/order", { method: "POST", body, timeoutMs: ORDER_TIMEOUT_MS }),
  },
  // The up-and-over door: standalone and internal-only, with its own order (one line on the rep's document).
  upAndOver: {
    init: () => request<UpAndOverInitResponse>("/up-and-over/init"),
    update: (state: UpAndOverDoorState) =>
      request<UpAndOverUpdateResponse>("/up-and-over/update", { method: "POST", body: state }),
    load: (projectId: number) => request<UpAndOverLoadResponse>(`/up-and-over/load/${projectId}`),
    order: (body: { state: UpAndOverDoorState; projectName: string; projectId: number | null; salesId: string | null }) =>
      request<UpAndOverOrderResponse>("/up-and-over/order", { method: "POST", body, timeoutMs: ORDER_TIMEOUT_MS }),
  },
  garage: {
    init: () => request<GarageInitResponse>("/garage/init"),
    load: (projectId: number) => request<GarageLoadResponse>(`/garage/load/${projectId}`),
    update: (state: unknown) =>
      request<GarageUpdateResponse>("/garage/update", { method: "POST", body: state }),
    getPrice: (state: unknown) =>
      request<PriceSummary>("/garage/price", { method: "POST", body: state }),
    // What shipping an order of several products to the dealer's own address costs: every product's ONE
    // unit net and how many, read against the account's postal code. Null when there is no figure to give.
    dealerFreight: (products: { net: number; quantity: number }[]) =>
      request<{ dealerFreight: number | null }>("/garage/dealer-freight", { method: "POST", body: { products } }),
    applyWindowTemplate: (body: unknown) =>
      request<unknown>("/garage/apply-window-template", { method: "POST", body }),
    applyVentilationTemplate: (body: unknown) =>
      request<unknown>("/garage/apply-ventilation-template", { method: "POST", body }),
    getSummary: (state: unknown) =>
      request<GarageSummaryResponse>("/garage/summary", { method: "POST", body: state }),
    getCost: (state: unknown) =>
      request<ProductCost>("/garage/cost", { method: "POST", body: state, timeoutMs: COST_TIMEOUT_MS }),

    getModelTransforms: () => request<Record<string, ModelTransformOverride>>("/admin/model3d/transforms"),

    upsertModelTransform: (key: string, transform: ModelTransformOverride) => request<ModelTransformOverride>
        ("/admin/model3d/transforms", {method: "PUT", body: { key, transform },}),

    addModelPositionOffset: (key: string, offset: Vec3D) => request<ModelTransformOverride>("/admin/model3d/transforms/position-offset", {
        method: "POST", body: { key, offset },}),

    setModelOrientation: (key: string, orientation: Vec3D) =>request<ModelTransformOverride>("/admin/model3d/transforms/orientation", {
        method: "POST", body: { key, orientation },}),

    setModelRotation: (key: string, rotation: number) =>request<ModelTransformOverride>("/admin/model3d/transforms/rotation", {
        method: "POST", body: { key, rotation },}),

    removeModelTransform: (key: string) =>request<void>("/admin/model3d/transforms/remove", {
        method: "POST", body: { key },}),

    generatePdf: (data: unknown) =>
      request<unknown>("/garage/pdf", { method: "POST", body: data, timeoutMs: DOCUMENT_TIMEOUT_MS }),
    generateConfigurationPdf: (data: unknown) =>
      request<unknown>("/garage/pdf/configuration", { method: "POST", body: data, timeoutMs: DOCUMENT_TIMEOUT_MS }),
    sendQuotation: (body: unknown) =>
      request<{ success: boolean; projectId: number | null; error?: string; quotationNumber?: number | null }>(
        "/garage/quotation/send", { method: "POST", body, timeoutMs: DOCUMENT_TIMEOUT_MS }),
    requestDealerQuotations: (body: unknown) =>
      request<{ success: boolean; sent: number; error?: string; quotationNumber?: number | null }>(
        "/garage/quotation/request-dealers", { method: "POST", body, timeoutMs: DOCUMENT_TIMEOUT_MS }),
    // Basic account + single project (#33).
    accountAvailable: () =>
      request<{ available: boolean }>("/account/available"),
    registerBasicAccount: (body: unknown) =>
      request<{ success: boolean; projectId?: number | null; error?: string; code?: string }>(
        "/account/register", { method: "POST", body, timeoutMs: DOCUMENT_TIMEOUT_MS }),
    saveSingleProject: (body: unknown) =>
      request<{ success: boolean; projectId?: number | null }>(
        "/garage/project/save-single", { method: "POST", body }),
    isQuotationSent: (id: number) =>
      request<{ isSent: boolean }>(`/garage/quotation/is-sent/${id}`),
    quotationDefaults: (id: number) =>
      request<{ name: string | null; phone: string | null; email: string | null; addressStreet: string | null; addressZipCode: string | null; addressCity: string | null }>(
        `/garage/quotation/defaults/${id}`),
    placeOrder: (data: unknown) =>
      request<PlaceOrderResponse>("/garage/order", { method: "POST", body: data, timeoutMs: ORDER_TIMEOUT_MS }),
    // Order a SAVED project: the server loads the persisted state itself (same access checks as
    // /garage/load), so the dashboard never round-trips the state through the browser.
    placeOrderForProject: (projectId: number) =>
      request<PlaceOrderResponse>(`/garage/order/${projectId}`, { method: "POST", timeoutMs: ORDER_TIMEOUT_MS }),

    getFaq: () => request<FaqItem[]>("/faq"),
    sendSupport: (body: { subject: string; message: string; locale: string; pageUrl: string }) =>
      request<{ success: boolean; error?: string }>("/support", { method: "POST", body }),
  },

  // Admin-only. The server gates every route on AppPermission.Admin, so the UI gate is
  // convenience — a non-admin hitting these directly gets a 403 regardless.
  admin: {
    /**
     * The emails that can be previewed, each with the things about it that can be changed.
     *
     * An axis is either a set of alternatives (`single`) or a set of switches that combine. The
     * server decides which is which, because it is the server that knows a section sitting inside
     * another one cannot be shown without it.
     */
    emailPreviews: () =>
      request<{ template: string; title: string; description: string;
                axes: { id: string; label: string; single: boolean;
                        options: { id: string; label: string; excludes: string[] | null }[] }[] }[]>(
        "/admin/email-previews"),

    /**
     * The sample-data preview, as HTML rather than JSON.
     *
     * Fetched, not linked. The route is inside the authorized /api/admin group, and a plain
     * <a href> or iframe src sends no bearer token — which works against the DevAuth bypass and
     * 401s in every deployment that validates B2C tokens.
     */
    emailPreviewSample: (template: string, flags: string[], language?: string, tenant?: string | null) =>
      request<string>(
        // Comma-separated, so the whole selection travels in one parameter and the resulting address
        // is something you can paste to somebody else.
        `/admin/email-previews/${encodeURIComponent(template)}?variant=${encodeURIComponent(flags.join(","))}`
        + (language ? `&lang=${encodeURIComponent(language)}` : "")
        + (tenant ? `&tenant=${encodeURIComponent(tenant)}` : ""),
        { responseType: "text" },
      ),

    /**
     * The body the engine would be posted for a saved project — the order path's own, minus the order
     * confirmation and with the session's parts taken from the project.
     *
     * Text rather than a parsed object: the caller writes it to a file, and re-serialising it here
     * would reformat the very thing the reader is trying to compare against a log.
     */
    enginePayload: (projectId: number) =>
      request<string>(`/admin/engine-payload/${projectId}`, { responseType: "text" }),

    /**
     * A quote request, looked up by the number the customer was given.
     *
     * By NUMBER rather than by project id: one request is a row per dealer, all carrying that number,
     * and the number is what a customer quotes down the phone and what our own escalation mail names.
     */
    quoteRequest: (quotationNumber: number) =>
      request<QuoteRequestView>(`/admin/quote-requests/${quotationNumber}`),

    /**
     * Hand an unanswered request to another dealer.
     *
     * This SENDS it to them, and cannot be taken back. The dealer who had it keeps their copy; the new
     * one gets its own, on the same quotation number.
     */
    assignQuoteRequest: (quotationNumber: number, dealerId: string) =>
      request<{ success: boolean; projectId?: number; error?: string }>(
        `/admin/quote-requests/${quotationNumber}/assign`,
        { method: "POST", body: { dealerId } },
      ),

    /** The written guides that ship with the app. */
    handbooks: () =>
      request<{ id: string; file: string; title: string; language: string; group: "handbook" | "tracking" }[]>("/admin/handbooks"),

    /**
     * One guide's HTML. Fetched rather than linked because the route requires authorization, and a
     * plain <a href> carries no bearer token — the caller turns this into a blob URL to open it.
     */
    handbook: (id: string) =>
      request<string>(`/admin/handbooks/${encodeURIComponent(id)}`, { responseType: "text" }),

    /**
     * Render from what the caller has open in the configurator — their door, the customer details they
     * typed, their own dealer. Answers with the email HTML itself, not JSON.
     *
     * The dealer is NOT in the body: the server takes it from the session, so a preview can only be
     * rendered under the caller's own identity.
     */
    renderEmailPreview: async (body: EmailPreviewRequest) => {
      let fallbacks: string[] = [];
      const html = await request<string>("/admin/email-previews/render", {
        method: "POST",
        body,
        responseType: "text",
        // What the render ACTUALLY used. Not derivable here: a dealer id can be present and still resolve
        // to nothing, and the resulting email looks like a styling glitch rather than missing data.
        onHeaders: (h) =>
          (fallbacks = (h.get("X-Preview-Fallbacks") ?? "").split(",").filter(Boolean)),
      });
      return { html, fallbacks };
    },

    /** Send the SAME render to a chosen address, to see what a real mail client makes of it. */
    sendEmailPreview: (to: string, preview: EmailPreviewRequest) =>
      request<{ sent: boolean; to: string }>("/admin/email-previews/send", {
        method: "POST",
        body: { to, preview },
        timeoutMs: DOCUMENT_TIMEOUT_MS,
      }),

    /** Retention preview/purge. Without `apply` it reports what WOULD go and changes nothing. */
    purgeExpired: (apply: boolean, days?: number) =>
      request<{ draftProjects: number; orphanedCustomers: number; dryRun: boolean }>(
        `/admin/privacy/purge${qs({ apply: apply || undefined, days })}`, { method: "POST", timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

    /** Right to erasure, by email. Same dry-run-by-default contract as the purge. */
    eraseSubject: (email: string, apply: boolean) =>
      request<{
        projectsDeleted: number; projectsAnonymised: number; customerRecords: number;
        userAccountRemoved: boolean; dryRun: boolean;
      }>("/admin/privacy/erase", { method: "POST", body: { email, apply: apply || undefined }, timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

    /**
     * The one-off pass that copies each project's customer from its summary form to the project list, and writes
     * customers imported from the old configurator into the form on their own project. Without `apply` it reports
     * what WOULD change and changes nothing. Counts only, never names or addresses.
     */
    fillCustomerColumns: (apply: boolean) =>
      request<{
        scanned: number; columnsFilled: number; namesChanged: number; legacyBlocksWritten: number;
        skippedMoved: number; unreadable: number; dryRun: boolean;
      }>(`/admin/privacy/customer-columns${qs({ apply: apply || undefined })}`, { method: "POST", timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

    /** The follow-up mail — dealer reminders and escalations — previewed, or sent with `apply`. */
    runFollowUps: (apply: boolean) =>
      request<{ dealerReminders: number; escalations: number; inactiveUserEmails: number; dryRun: boolean }>(
        `/admin/privacy/follow-ups${qs({ apply: apply || undefined })}`, { method: "POST", timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

    /** Fills a blank name or e-mail on a user row from the directory. Counts only. */
    backfillUserNames: () =>
      request<{ examined: number; namesFilled: number; emailsFilled: number }>(
        "/admin/users/backfill-names", { method: "POST", timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

    /** A plain link, not a fetch: the browser's own download handles the file and the filename. */
    userActivityCsvUrl: (configuratorType?: string) =>
      `/api/admin/users/activity.csv${qs({ configuratorType })}`,

    deadLetteredOrderJobs: (limit?: number) =>
      request<OrderJobDto[]>(`/admin/order-jobs/dead-letters${qs({ limit })}`),

    requeueOrderJob: (intentId: string) =>
      request<OrderJobActionResult>(`/admin/order-jobs/${encodeURIComponent(intentId)}/requeue`, {
        method: "POST",
      }),

    resolveOrderJob: (intentId: string, note?: string) =>
      request<OrderJobActionResult>(
        `/admin/order-jobs/${encodeURIComponent(intentId)}/resolve${qs({ note })}`,
        { method: "POST" },
      ),

    users: (search?: string) => request<AdminUser[]>(`/admin/users${qs({ search })}`),

    /**
     * Everything the analytics tab draws, for one stretch of time. `from` and `to` are yyyy-MM-dd and
     * only mean anything when `range` is "custom"; the server answers a month for anything it cannot
     * read, and says in `range` which window it actually used.
     */
    analytics: (range: AnalyticsRangeName, from?: string, to?: string) =>
      request<Analytics>(`/admin/analytics${qs({ range, from, to })}`),

    /** Every dealer's account number and name. Admin-only; the dashboard labels its rows with it. */
    dealerNames: () => request<DealerName[]>("/admin/dealer-names"),

    setUserPermission: (email: string, permission: AppPermissionName, granted: boolean) =>
      request<{ ok: boolean }>("/admin/users/permissions", {
        method: "POST",
        body: { email, permission, granted },
      }),

    startCacheRefresh: () => request<CacheRefreshStarted>("/admin/cache/refresh", { method: "POST" }),

    // 204 (no run on record) comes back as null — the engine holds this in memory, so a restart
    // loses the history and that is not an error.
    cacheRefreshStatus: (jobId?: string) =>
      request<CacheRefreshStatus | null>(`/admin/cache/refresh/status${qs({ jobId })}`),

    // The price list read again from SharePoint, by the engine and then into this app's catalog. 422 means
    // the engine refused the file and kept the prices it had; the error's message is its reason.
    reloadPriceList: () =>
      request<PriceListReloaded>("/admin/cache/price-list", { method: "POST", timeoutMs: PRICE_LIST_TIMEOUT_MS }),

    // The tenant register. Gated on Admin OR TenantBranding server-side — the one admin surface
    // marketing reaches without holding Admin.
    tenants: {
      list: () => request<TenantSummary[]>("/admin/tenants"),

      create: (id: string, name: string) =>
        request<TenantEditor>("/admin/tenants", { method: "POST", body: { id, name } }),

      get: (id: string) => request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}`),

      save: (id: string, body: SaveTenantRequest) =>
        request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}`, { method: "PUT", body }),

      archive: (id: string) =>
        request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}/archive`, { method: "POST" }),

      unarchive: (id: string) =>
        request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}/unarchive`, { method: "POST" }),

      /** The measurement setup: the version in force, the retired ones, and what the site receives. */
      tracking: (id: string) => request<TenantTracking>(`/admin/tenants/${encodeURIComponent(id)}/tracking`),

      /** Puts the next version in force. Never an edit: the current version is retired under it. */
      saveTracking: (id: string, body: SaveTenantTrackingRequest) =>
        request<TenantTracking>(`/admin/tenants/${encodeURIComponent(id)}/tracking`, { method: "POST", body }),

      /** What GA4's validation server says about the events staged for this tenant. Nothing is sent to the property. */
      validateGa4: (id: string) =>
        request<Ga4ValidationReport>(`/admin/tenants/${encodeURIComponent(id)}/tracking/validate-ga4`,
          { method: "POST", timeoutMs: ADMIN_RUN_TIMEOUT_MS }),

      /** The brochures: every language a PDF may be uploaded in, with every slot's policy. */
      brochures: (id: string) => request<TenantBrochures>(`/admin/tenants/${encodeURIComponent(id)}/brochures`),

      /** The PDF itself as the body; the server counts its pages before it stores anything. */
      uploadBrochure: (id: string, language: string, pdf: Blob) =>
        request<TenantBrochures>(`/admin/tenants/${encodeURIComponent(id)}/brochures/${encodeURIComponent(language)}`, {
          method: "PUT",
          body: pdf,
          headers: { "Content-Type": "application/pdf" },
        }),

      deleteBrochure: (id: string, language: string) =>
        request<TenantBrochures>(`/admin/tenants/${encodeURIComponent(id)}/brochures/${encodeURIComponent(language)}`, {
          method: "DELETE",
        }),

      /** The slot policies, replaced whole. */
      saveBrochureSlots: (id: string, language: string, slots: SaveBrochureSlotRequest[]) =>
        request<TenantBrochures>(`/admin/tenants/${encodeURIComponent(id)}/brochures/${encodeURIComponent(language)}/slots`, {
          method: "PUT",
          body: slots,
        }),

      /**
       * The tenant's own GTM container, tailored from the committed export and ready to import.
       *
       * `onHeaders` carries the server's own file name, which names the container the file belongs in —
       * the first thing the import guide asks the importer to check.
       */
      gtmContainer: (id: string, onHeaders?: (headers: Headers) => void) =>
        request<Blob>(`/admin/tenants/${encodeURIComponent(id)}/gtm-container.json`, {
          responseType: "blob",
          onHeaders,
        }),

      previewPdf: (id: string, language: string, kind: PdfDocumentKind) =>
        request<Blob>(
          `/admin/tenants/${encodeURIComponent(id)}/preview.pdf?lang=${encodeURIComponent(language)}&kind=${encodeURIComponent(kind)}`,
          { responseType: "blob", timeoutMs: DOCUMENT_TIMEOUT_MS },
        ),

      /** The PNG itself as the body; the server checks the signature, not the header. */
      uploadLogo: (id: string, png: Blob) =>
        request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}/logo`, {
          method: "PUT",
          body: png,
          headers: { "Content-Type": "image/png" },
        }),

      deleteLogo: (id: string) =>
        request<TenantEditor>(`/admin/tenants/${encodeURIComponent(id)}/logo`, { method: "DELETE" }),
    },

    // A tenant's promotions. Under the tenants' route shape because a campaign belongs to exactly one
    // brand, and behind the same gate: Admin OR TenantBranding, which is marketing's.
    campaigns: {
      list: (tenantId: string) =>
        request<Campaign[]>(`/admin/tenants/${encodeURIComponent(tenantId)}/campaigns`),

      create: (tenantId: string, body: SaveCampaignRequest) =>
        request<Campaign>(`/admin/tenants/${encodeURIComponent(tenantId)}/campaigns`, { method: "POST", body }),

      save: (tenantId: string, campaignId: number, body: SaveCampaignRequest) =>
        request<Campaign>(`/admin/tenants/${encodeURIComponent(tenantId)}/campaigns/${campaignId}`, {
          method: "PUT",
          body,
        }),

      remove: (tenantId: string, campaignId: number) =>
        request<void>(`/admin/tenants/${encodeURIComponent(tenantId)}/campaigns/${campaignId}`, {
          method: "DELETE",
        }),
    },

    // Everything, including switched-off and lapsed — this is where notices are managed.
    notices: () => request<Notice[]>("/admin/notices"),

    createNotice: (body: SaveNoticeRequest) =>
      request<Notice>("/admin/notices", { method: "POST", body }),

    updateNotice: (id: number, body: SaveNoticeRequest) =>
      request<Notice>(`/admin/notices/${id}`, { method: "PUT", body }),

    deleteNotice: (id: number) =>
      request<{ ok: boolean }>(`/admin/notices/${id}`, { method: "DELETE" }),
  },

  notices: {
    /** Only what is currently visible — the server applies the active + not-expired filter. */
    active: () => request<Notice[]>("/notices/active"),
  },

  /**
   * What changed in the app itself. Distinct from `notices`, which serves announcements somebody
   * wrote in the admin area: these ship with the build that changed, so deploying is what publishes.
   */
  releaseNotes: {
    /**
     * The whole history, newest first, each release flagged `isNew` for THIS reader. The flag is the
     * server's to set — version order is a rule, not a string sort, and a client that got it wrong
     * would either nag about old news or swallow new news entirely.
     */
    list: () => request<ReleaseNotes>("/release-notes"),

    /**
     * Records that this person has read up to the version the build is on. No body: the dialog shows
     * everything unseen at once, so closing it acknowledges all of it.
     */
    markSeen: () =>
      request<{ lastSeenVersion: string | null }>("/release-notes/seen", { method: "POST" }),
  },
};
